import { useEffect, useMemo, useState } from "react";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "http://127.0.0.1:8000";

function normaliseFileUrl(src) {
  if (!src) return "";
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  if (src.startsWith("/media/")) return `${API_ORIGIN}${src}`;
  if (src.startsWith("media/")) return `${API_ORIGIN}/${src}`;
  return src;
}

const pageConfig = {
  categories: {
    label: "Categories",
    eyebrow: "MARKETPLACE TAXONOMY",
    description: "Category and subcategory structure used by sellers and business accounts.",
    loader: phase1Api.categories,
    columns: ["code", "name", "parent_name", "risk_level", "requires_manual_product_review", "is_active"],
  },
  products: {
    label: "Product catalogue",
    eyebrow: "MARKETPLACE",
    description: "Platform-wide catalogue moderation. Sellers add products from their Products workspace; admin controls visibility and flagged listings.",
    loader: phase1Api.products,
    columns: ["code", "title", "category_name", "seller_display", "price_range", "stock_status", "status"],
    actions: [
      { key: "approve", label: "Publish", action: "APPROVE", icon: "check", tone: "success", handler: phase1Api.productDecision },
      { key: "review", label: "Review", action: "REVIEW", icon: "review", tone: "warning", handler: phase1Api.productDecision },
      { key: "feature", label: "Feature", action: "FEATURE", icon: "star", tone: "info", handler: phase1Api.productDecision },
      { key: "reject", label: "Reject", action: "REJECT", icon: "x", tone: "danger", handler: phase1Api.productDecision },
    ],
  },
  sellers: {
    label: "Seller onboarding",
    eyebrow: "VERIFICATION",
    description: "Seller applications are reviewed against store profile data, selling categories and verification documents before approval.",
    loader: phase1Api.sellers,
    columns: ["code", "store_name", "city", "status", "verification_status", "user_is_verified"],
    actions: [
      { key: "require_docs", label: "Request company documents", icon: "review", tone: "info", handler: phase1Api.sellerRequirements, payload: { requires_incorporation_certificate: true, requires_tax_clearance: true, notes: "COMPANY DOCUMENTS REQUIRED" } },
      { key: "approve", label: "Approve seller", action: "APPROVE", icon: "check", tone: "success", handler: phase1Api.sellerDecision },
      { key: "review", label: "Mark under review", action: "REVIEW", icon: "review", tone: "warning", handler: phase1Api.sellerDecision },
      { key: "reject", label: "Reject seller", action: "REJECT", icon: "x", tone: "danger", handler: phase1Api.sellerDecision },
      { key: "suspend", label: "Suspend seller", action: "SUSPEND", icon: "lock", tone: "danger", handler: phase1Api.sellerDecision },
    ],
  },
  businesses: {
    label: "Business accounts",
    eyebrow: "MERCHANT ERP",
    description: "Larger shops and merchant accounts that need stock, goods movement and subscription tools.",
    loader: phase1Api.businesses,
    columns: ["code", "trading_name", "business_type", "branch_count", "status", "verification_status"],
    actions: [
      { key: "approve", label: "Approve business", action: "APPROVE", icon: "check", tone: "success", handler: phase1Api.businessDecision },
      { key: "review", label: "Mark under review", action: "REVIEW", icon: "review", tone: "warning", handler: phase1Api.businessDecision },
      { key: "reject", label: "Reject business", action: "REJECT", icon: "x", tone: "danger", handler: phase1Api.businessDecision },
      { key: "suspend", label: "Suspend business", action: "SUSPEND", icon: "lock", tone: "danger", handler: phase1Api.businessDecision },
    ],
  },
  buyers: {
    label: "Buyer accounts",
    eyebrow: "ACCOUNTS",
    description: "Buyer accounts using the public marketplace and order workflow.",
    loader: phase1Api.buyers,
    columns: ["display_name", "email", "phone", "status", "verification_status"],
  },
  transporters: {
    label: "Transporter network",
    eyebrow: "LOGISTICS",
    description: "Courier and transporter profiles available for delivery selection.",
    loader: phase1Api.couriers,
    columns: ["code", "transporter_name", "vehicle_type", "base_city", "status", "verification_status"],
    actions: [
      { key: "approve", label: "Approve transporter", action: "APPROVE", icon: "check", tone: "success", handler: phase1Api.courierDecision },
      { key: "review", label: "Mark under review", action: "REVIEW", icon: "review", tone: "warning", handler: phase1Api.courierDecision },
      { key: "reject", label: "Reject transporter", action: "REJECT", icon: "x", tone: "danger", handler: phase1Api.courierDecision },
      { key: "suspend", label: "Suspend transporter", action: "SUSPEND", icon: "lock", tone: "danger", handler: phase1Api.courierDecision },
    ],
  },
  documents: {
    label: "Document review",
    eyebrow: "TRUST & COMPLIANCE",
    description: "Open each uploaded file, check whether it matches the declared document type, then approve, reject or place it under review with notes.",
    loader: phase1Api.documents,
    columns: ["owner_name", "owner_account_type", "document_type_label", "title", "document_number", "status", "reviewed_at", "review_notes"],
  },
  enquiries: {
    label: "Order enquiries",
    eyebrow: "TRADE FLOW",
    description: "Buyer enquiries routed to sellers before order conversion.",
    loader: phase1Api.enquiries,
    columns: ["code", "buyer_name", "product_title", "seller_name", "quantity", "status"],
  },
  orders: {
    label: "Marketplace orders",
    eyebrow: "TRANSACTIONS",
    description: "Order records linking buyer, seller, payment and delivery status.",
    loader: phase1Api.orders,
    columns: ["order_number", "buyer_name", "seller_name", "status", "payment_status", "total_amount"],
  },
  deliveries: {
    label: "Delivery requests",
    eyebrow: "LOGISTICS",
    description: "Transporter assignment and delivery request workflow.",
    loader: phase1Api.deliveries,
    columns: ["code", "order_number", "courier_name", "requested_service", "status", "quoted_amount"],
  },
  subscriptions: {
    label: "Subscription plans",
    eyebrow: "BUSINESS ACCESS",
    description: "Monthly plans for sellers, couriers and larger business accounts.",
    loader: phase1Api.subscriptionPlans,
    columns: ["code", "name", "audience", "monthly_price", "max_products", "is_active"],
  },
};

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "YES" : "NO";
  return String(value);
}

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["APPROVED", "ACTIVE", "VERIFIED", "PUBLISHED", "PAID", "COMPLETED", "AVAILABLE", "IN_STOCK"].includes(status)) return "good";
  if (["PENDING", "PENDING_APPROVAL", "UNDER_REVIEW", "NEW", "REQUESTED", "PENDING_SELLER"].includes(status)) return "warn";
  if (["REJECTED", "SUSPENDED", "FAILED", "CANCELLED", "ARCHIVED", "OUT_OF_STOCK", "UNAVAILABLE", "DEFECTIVE_BATCH"].includes(status)) return "bad";
  return "neutral";
}

function formatActionError(err) {
  const data = err?.response?.data || {};
  if (data.missing_documents?.length) {
    return `${data.detail || "Action failed."} Missing: ${data.missing_documents.join(", ")}. Uploaded documents must be approved under Document Review before they count.`;
  }
  return data.detail || "Action failed. Check your access rights or try again.";
}

function ActionIcon({ name }) {
  const paths = {
    check: <path d="m5 12.5 4.2 4.2L19 7" />,
    x: <><path d="M6.5 6.5 17.5 17.5" /><path d="m17.5 6.5-11 11" /></>,
    review: <><path d="M5 12h14" /><path d="M12 5v14" /></>,
    lock: <><path d="M7 10V8a5 5 0 0 1 10 0v2" /><path d="M6 10h12v10H6z" /></>,
    star: <path d="m12 4 2.3 4.8 5.2.7-3.8 3.7.9 5.2L12 16l-4.6 2.4.9-5.2-3.8-3.7 5.2-.7L12 4Z" />,
    eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><path d="M12 9.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6Z" /></>,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name] || paths.review}
    </svg>
  );
}

function DocumentReviewModal({ document, notes, setNotes, actionMessage, busy, onClose, onDecision }) {
  if (!document) return null;
  const fileUrl = normaliseFileUrl(document.file_url || "");
  const isImage = /\.(png|jpg|jpeg|webp|gif)$/i.test(fileUrl);
  const isPdf = /\.pdf($|\?)/i.test(fileUrl);

  return (
    <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
      <section className="tazy-modal-card document-review-modal document-review-modal-v2">
        <header className="tazy-modal-header document-review-header-v2">
          <div>
            <span>DOCUMENT REVIEW</span>
            <h3>{document.document_type_label || document.document_type}</h3>
            <p>Check the file before taking action. Approval should only confirm that the uploaded file matches the declared document type.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close modal">×</button>
        </header>

        <div className="document-review-layout document-review-layout-v2">
          <aside className="document-review-meta document-review-meta-v2">
            <div className="document-review-summary-card">
              <span>Owner</span>
              <strong>{document.owner_name || "—"}</strong>
              <em>{document.owner_account_type || "ACCOUNT"}</em>
            </div>

            <div className="document-review-info-grid">
              <div><span>Document type</span><strong>{document.document_type_label || document.document_type || "—"}</strong></div>
              <div><span>Document number</span><strong>{document.document_number || "—"}</strong></div>
              <div><span>Status</span><strong><span className={`status-chip ${statusTone(document.status)}`}>{document.status_label || document.status}</span></strong></div>
              <div><span>Reviewed</span><strong>{document.reviewed_at ? new Date(document.reviewed_at).toLocaleString() : "Not reviewed"}</strong></div>
            </div>

            <label className="document-review-notes-label">Review notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Example: National ID name matches the seller account. File is readable and valid for verification." rows="5" />

            {actionMessage && <div className="document-action-inline-message">{actionMessage}</div>}
            <div className="document-review-actions document-review-actions-v2 sticky-document-actions">
              <button type="button" disabled={busy || !fileUrl} className="review-btn success" onClick={() => onDecision("APPROVE")}>{busy ? "Working..." : "Approve document"}</button>
              <button type="button" disabled={busy || !fileUrl} className="review-btn warning" onClick={() => onDecision("REVIEW")}>{busy ? "Working..." : "Keep under review"}</button>
              <button type="button" disabled={busy} className="review-btn danger" onClick={() => onDecision("REJECT")}>{busy ? "Working..." : "Reject document"}</button>
            </div>
          </aside>

          <div className="document-preview-pane document-preview-pane-v2">
            <div className="document-preview-toolbar">
              <span>{fileUrl ? "Attached file" : "No attachment"}</span>
              {fileUrl && <a href={fileUrl} target="_blank" rel="noreferrer">Open in new tab</a>}
            </div>
            <div className="document-preview-frame">
              {fileUrl ? (
                isImage ? (
                  <img src={fileUrl} alt={document.title || "Document preview"} />
                ) : isPdf ? (
                  <object data={fileUrl} type="application/pdf">
                    <div className="document-preview-empty">PDF preview is not available in this browser view. Use “Open in new tab” to review the file before approving it.</div>
                  </object>
                ) : (
                  <div className="document-preview-empty">This file type cannot be previewed here. Open it in a new tab before approving.</div>
                )
              ) : (
                <div className="document-preview-empty">No file attached. This document cannot be approved.</div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function PortalRecordsPage({ type }) {
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actioning, setActioning] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewActionMessage, setReviewActionMessage] = useState("");

  const config = pageConfig[type] || pageConfig.products;
  const canAct = Boolean(user?.is_platform_staff || user?.account_type === "SUPER_ADMIN" || user?.permissions?.includes("*"));

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    config.loader()
      .then((res) => {
        if (mounted) setRows(Array.isArray(res.data) ? res.data : res.data?.results || []);
      })
      .catch(() => {
        if (mounted) setError("Unable to load records right now.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [type, reloadKey, config]);

  const visibleRows = useMemo(() => rows.slice(0, 50), [rows]);

  async function handleAction(action, row) {
    if (!action?.handler || !row?.id) return;
    const isRisky = ["REJECT", "SUSPEND", "ARCHIVE"].includes(action.action);
    if (isRisky && !window.confirm(`${action.label}?`)) return;

    setActioning(`${row.id}-${action.key}`);
    setError("");
    try {
      const payload = action.payload || { action: action.action };
      await action.handler(row.id, payload);
      setReloadKey((value) => value + 1);
    } catch (err) {
      const msg = formatActionError(err);
      setReviewActionMessage(msg);
      setError(msg);
    } finally {
      setActioning("");
    }
  }

  function openDocumentReview(row) {
    setReviewDoc(row);
    setReviewNotes(row.review_notes || "");
    setReviewActionMessage("");
  }

  async function decideDocument(action) {
    if (!reviewDoc?.id) return;
    setActioning(`${reviewDoc.id}-${action}`);
    setError("");
    setReviewActionMessage("");
    try {
      await phase1Api.documentDecision(reviewDoc.id, { action, notes: reviewNotes });
      setReviewActionMessage(`Document ${action.toLowerCase().replace("_", " ")} completed.`);
      setReviewDoc(null);
      setReviewNotes("");
      setReloadKey((value) => value + 1);
    } catch (err) {
      const msg = formatActionError(err);
      setReviewActionMessage(msg);
      setError(msg);
    } finally {
      setActioning("");
    }
  }

  return (
    <div className={`portal-shell-v2 refined-portal-shell ${sidebarCollapsed ? "is-collapsed" : ""}`}>
      <PortalSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((value) => !value)} />
      <main className="portal-main-v2 records-main-clean">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="records-hero phase1b-records-hero">
          <div>
            <span>{config.eyebrow}</span>
            <h2>{config.label}</h2>
            <p>{config.description}</p>
          </div>
          <strong>{rows.length}</strong>
        </section>

        {error && <div className="portal-warning">{error}</div>}

        <section className="records-card phase1b-records-card">
          <div className="records-card__header">
            <div>
              <span>LIVE BACKEND DATA</span>
              <h3>{config.label}</h3>
            </div>
            <button type="button" onClick={() => setReloadKey((value) => value + 1)}>Refresh records</button>
          </div>

          {loading ? (
            <div className="records-empty">Loading records...</div>
          ) : visibleRows.length === 0 ? (
            <div className="records-empty">No records yet. Seed data or create records through the portal/API.</div>
          ) : (
            <div className="records-table-wrap">
              <table className="records-table phase1b-table compact-records-table">
                <thead>
                  <tr>
                    {config.columns.map((column) => <th key={column}>{column.replaceAll("_", " ")}</th>)}
                    {type === "documents" ? <th>Review</th> : null}
                    {canAct && config.actions?.length ? <th>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, index) => (
                    <tr key={row.id || row.code || row.order_number || index}>
                      {config.columns.map((column) => {
                        const value = formatValue(row[column]);
                        const isStatusColumn = column.includes("status") || column === "is_active";
                        return (
                          <td key={column}>
                            {isStatusColumn ? <span className={`status-chip ${statusTone(value)}`}>{value}</span> : value}
                          </td>
                        );
                      })}
                      {type === "documents" && (
                        <td>
                          <button type="button" className="document-review-open" onClick={() => openDocumentReview(row)}>
                            <ActionIcon name="eye" /> Review file
                          </button>
                        </td>
                      )}
                      {canAct && config.actions?.length ? (
                        <td>
                          <div className="record-actions" aria-label="Record actions">
                            {config.actions.map((action) => (
                              <button
                                key={action.key}
                                type="button"
                                className={`record-action-btn ${action.tone || "neutral"}`}
                                title={action.label}
                                aria-label={action.label}
                                disabled={actioning === `${row.id}-${action.key}`}
                                onClick={() => handleAction(action, row)}
                              >
                                <ActionIcon name={action.icon} />
                              </button>
                            ))}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <DocumentReviewModal
        document={reviewDoc}
        notes={reviewNotes}
        setNotes={setReviewNotes}
        actionMessage={reviewActionMessage}
        busy={Boolean(actioning)}
        onClose={() => { setReviewDoc(null); setReviewActionMessage(""); }}
        onDecision={decideDocument}
      />
    </div>
  );
}
